/**
 * GuessChatFeed: V4.2.T10b public wrong-guess feed (D2, §5.12 / §C9).
 *
 * Renders the BOUNDED, server-redacted `chatLog` from the ws store in the
 * drawing-phase RIGHT column. The server is the trust boundary: the drawer
 * never receives `miss` text, and `correct` lines carry no word, so this
 * component renders whatever it's handed, verbatim.
 *
 * Readability / a11y (§C9):
 *  - 13px muted body text; the author NAME tints to the player's BRAND_COLOR
 *    (wire-coupled: `line.color`).
 *  - `correct` lines are visually distinct (--correct accent, compact "guessed
 *    it!" with no quoted text).
 *  - ~8 visible rows with a top fade; the list scrolls.
 *  - Auto-scroll to newest ONLY when the viewer was already pinned to the
 *    bottom (so reading older lines isn't yanked away).
 *  - OPACITY-ONLY entry animation (no per-row slide that pulls the eye off the
 *    canvas); disabled entirely under prefers-reduced-motion (CSS).
 *  - Empty state: "No guesses yet, be the first!"
 *  - `role="log" aria-live="polite"`; each row's accessible name names the
 *    author so a screen reader announces "Alice guessed: is it a dog".
 */

import { useEffect, useRef } from "preact/hooks";
import type { ChatLine } from "../ws.js";
import { textSafeColor } from "./textSafeColor.js";

type GuessChatFeedProps = {
  /** Bounded, server-redacted chat lines (oldest → newest). */
  chatLog: ReadonlyArray<ChatLine>;
  /**
   * Role-aware empty-state line (audit 2026-06-11): the default invites a
   * guess, which the DRAWER and SPECTATORS can't make, callers pass their
   * own line for those roles.
   */
  emptyHint?: string;
};

/** Px slack for the "is the viewer pinned to the bottom?" check. */
const BOTTOM_SLACK_PX = 24;

export function GuessChatFeed({ chatLog, emptyHint }: GuessChatFeedProps) {
  const listRef = useRef<HTMLOListElement>(null);
  // Whether the viewer was at the bottom BEFORE this render's new line(s)
  // landed: captured in a layout effect so auto-scroll only fires when they
  // weren't scrolled up reading history.
  const pinnedRef = useRef(true);

  // Record pinned-ness on every scroll so the next append knows whether to
  // follow. (Runs cheaply; the list is short.)
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_SLACK_PX;
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  // After new lines render, follow to the bottom ONLY if we were pinned.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (pinnedRef.current) el.scrollTop = el.scrollHeight;
  }, [chatLog.length]);

  if (chatLog.length === 0) {
    return (
      <div class="guess-chat-feed guess-chat-feed--empty" data-testid="guess-chat-feed">
        <p class="guess-chat-empty" data-testid="guess-chat-empty">
          {emptyHint ?? "No guesses yet — be the first!"}
        </p>
      </div>
    );
  }

  return (
    <div class="guess-chat-feed" data-testid="guess-chat-feed">
      <ol
        ref={listRef}
        class="guess-chat-list"
        data-testid="guess-chat-list"
        role="log"
        aria-live="polite"
        aria-label="Guess feed"
      >
        {chatLog.map((line) => (
          <ChatRow key={line.key} line={line} />
        ))}
      </ol>
    </div>
  );
}

function ChatRow({ line }: { line: ChatLine }) {
  const accessibleName =
    line.kind === "correct"
      ? `${line.name} guessed correctly`
      : `${line.name} guessed: ${line.text ?? ""}`;
  return (
    <li
      class={`guess-chat-row guess-chat-row--${line.kind}`}
      data-testid="guess-chat-row"
      data-kind={line.kind}
      aria-label={accessibleName}
    >
      <span
        class="guess-chat-name"
        style={{ color: textSafeColor(line.color) }}
        data-testid="guess-chat-name"
      >
        {line.name}
      </span>{" "}
      {line.kind === "correct" ? (
        <span class="guess-chat-correct-label">guessed it!</span>
      ) : (
        <span class="guess-chat-text" data-testid="guess-chat-text">
          {line.text}
        </span>
      )}
    </li>
  );
}
